import React, { useMemo, useState } from "react";
import {
  Accordion,
  Badge,
  Box,
  Button,
  Group,
  Select,
  Text,
  TextInput,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { functionLinks } from "../data/navLinks";
import {
  actionAccessOptions,
  ACTION_ACCESS_HIDDEN,
  ACTION_ACCESS_DISABLED,
  ACTION_ACCESS_ACCESSIBLE,
  getEntitiesForPageLink,
  getRegistryEntry,
} from "../data/actionRegistry";

const pageAccessOptions = [
  { value: 0, label: "Hidden", color: "red" },
  { value: 1, label: "Read Only", color: "orange" },
  { value: 15, label: "Access", color: "green" },
];

const PageHeader = ({ page, index, pageRights, setFieldValue }) => {
  const entry = pageRights.find((r) => r.link === page.linkId);
  const right = entry?.accessRight ?? 0;
  const color =
    pageAccessOptions.find((r) => r.value === right)?.color ?? "red";

  return (
    <Group position="apart" style={{ width: "100%" }} noWrap>
      <Group align="start" sx={{ flex: 1, minWidth: 0 }}>
        <Badge color={color} size="xs" mt={5}>
          {index}
        </Badge>
        <Box sx={{ minWidth: 0 }}>
          <Text size="sm" weight={500} lineClamp={1}>
            {page.group ? `${page.group} > ${page.label}` : page.label}
          </Text>
          <Text size="xs" color="dimmed" lineClamp={1}>
            {page.link}
          </Text>
        </Box>
      </Group>
      <Box onClick={(e) => e.stopPropagation()}>
        <Select
          data={pageAccessOptions}
          size="xs"
          value={right}
          w={130}
          styles={{ input: { borderColor: color } }}
          onChange={(value) => {
            const newRights = [...pageRights];
            const idx = newRights.findIndex((r) => r.link === page.linkId);
            if (idx >= 0) {
              newRights[idx] = { ...newRights[idx], accessRight: value };
            } else {
              newRights.push({ link: page.linkId, accessRight: value });
            }
            setFieldValue("links", newRights);
          }}
        />
      </Box>
    </Group>
  );
};

const ActionRow = ({ entity, action, label, actionRights, setFieldValue }) => {
  const entry = actionRights.find(
    (r) => r.entity === entity && r.action === action.key
  );
  const right = entry?.accessRight ?? ACTION_ACCESS_HIDDEN;
  const color =
    actionAccessOptions.find((r) => r.value === right)?.color ?? "red";

  return (
    <Box mb="sm" key={`${entity}-${action.key}`}>
      <Group position="apart">
        <Text size="sm">{label}</Text>
        <Select
          data={actionAccessOptions}
          size="xs"
          value={right}
          w={130}
          styles={{ input: { borderColor: color } }}
          onChange={(value) => {
            const newRights = [...actionRights];
            const idx = newRights.findIndex(
              (r) => r.entity === entity && r.action === action.key
            );
            if (idx >= 0) {
              newRights[idx] = { ...newRights[idx], accessRight: value };
            } else {
              newRights.push({
                entity,
                action: action.key,
                accessRight: value,
              });
            }
            setFieldValue("actionLinks", newRights);
          }}
        />
      </Group>
    </Box>
  );
};

const EntityActionGroup = ({
  entityKey,
  actionRights,
  setFieldValue,
  onBulkSet,
}) => {
  const { t } = useTranslation();
  const entry = getRegistryEntry(entityKey);
  if (!entry) return null;

  return (
    <Box mb="lg">
      <Group position="apart" mb="xs">
        <Text size="xs" weight={600} transform="uppercase" color="dimmed">
          {entry.label}
        </Text>
        <Group spacing="xs">
          <Button
            size="xs"
            variant="subtle"
            onClick={() => onBulkSet(entityKey, ACTION_ACCESS_ACCESSIBLE)}
          >
            {t("All Accessible")}
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="orange"
            onClick={() => onBulkSet(entityKey, ACTION_ACCESS_DISABLED)}
          >
            {t("All Disable")}
          </Button>
          <Button
            size="xs"
            variant="subtle"
            color="red"
            onClick={() => onBulkSet(entityKey, ACTION_ACCESS_HIDDEN)}
          >
            {t("All Hidden")}
          </Button>
        </Group>
      </Group>
      {entry.actions.map((action) => (
        <ActionRow
          key={action.key}
          entity={entityKey}
          action={action}
          label={action.label}
          actionRights={actionRights}
          setFieldValue={setFieldValue}
        />
      ))}
    </Box>
  );
};

const PageAndActionAccessList = ({ links, actionLinks, setFieldValue }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const pages = [...functionLinks];
  const pageRights = links ?? [];
  const actionRights = actionLinks ?? [];

  const filteredPages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pages;

    return pages.filter((page) => {
      const haystack = [
        page.label,
        page.group,
        page.link,
        page.linkId,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (haystack.includes(q)) return true;

      const entities = getEntitiesForPageLink(page.linkId);
      return entities.some((entityKey) => {
        const entry = getRegistryEntry(entityKey);
        if (!entry) return false;
        return entry.actions.some((a) =>
          a.label.toLowerCase().includes(q)
        );
      });
    });
  }, [pages, search]);

  const setAllForEntity = (entity, accessRight) => {
    const entityActions = getRegistryEntry(entity)?.actions ?? [];
    const newRights = [...actionRights];
    entityActions.forEach(({ key }) => {
      const idx = newRights.findIndex(
        (r) => r.entity === entity && r.action === key
      );
      if (idx >= 0) {
        newRights[idx] = { ...newRights[idx], accessRight };
      } else {
        newRights.push({ entity, action: key, accessRight });
      }
    });
    setFieldValue("actionLinks", newRights);
  };

  return (
    <>
      <TextInput
        placeholder={t("Search pages or actions...")}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        mb="md"
      />

      {filteredPages.length === 0 && (
        <Text size="sm" color="dimmed">
          {t("No pages match your search.")}
        </Text>
      )}

      <Accordion variant="separated" radius="xs">
        {filteredPages.map((page, index) => {
          const entities = getEntitiesForPageLink(page.linkId);
          const itemKey = `${page.group ?? ""}::${page.linkId}::${page.link}`;

          return (
            <Accordion.Item key={itemKey} value={itemKey}>
              <Accordion.Control>
                <PageHeader
                  page={page}
                  index={index + 1}
                  pageRights={pageRights}
                  setFieldValue={setFieldValue}
                />
              </Accordion.Control>
              <Accordion.Panel>
                {entities.length === 0 ? (
                  <Text size="sm" color="dimmed">
                    {t("No configurable actions for this page.")}
                  </Text>
                ) : (
                  entities.map((entityKey) => (
                    <EntityActionGroup
                      key={entityKey}
                      entityKey={entityKey}
                      actionRights={actionRights}
                      setFieldValue={setFieldValue}
                      onBulkSet={setAllForEntity}
                    />
                  ))
                )}
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </>
  );
};

export default React.memo(
  PageAndActionAccessList,
  (prev, next) =>
    prev.links === next.links &&
    prev.actionLinks === next.actionLinks &&
    prev.setFieldValue === next.setFieldValue
);
